/**
 * @description TowTech SO Assets & Crew (LYN-408 / LYN-421 / LYN-425 / LYN-428 / LYN-430 / LYN-436 / LYN-446). Company and All.
 *
 * Key features:
 * - Time grain re-reads stored Utilization windows and updates status counts
 * - Show: All | Top performers | Bottom performers. Each opens the settings
 *   dropdown. Clicking the open choice again closes it. A click outside
 *   closes it. No Settings button. All hides How many.
 * - Callouts: Top 3 by Total Invoice, Highest Yield, Unpaid; revolving line named to Time
 * - One modal for truck and driver: tiles, story, chart, value line
 * - Chart axis starts at 0. Invoice | Calls switches the series.
 * - List shows 20 drivers or trucks, then pages.
 *
 * @dependencies assetsCrewService, assetsCrew, scope1Dashboard, ReportingCharts,
 *   PackPage, PackChromeContext, lucide-react
 *
 * @notes Company switcher stays in the rail. Company clocks companies.timezone.
 *   All clocks the viewer zone. No source re-query. No jobs write. No LynxPay.
 *   No RBAC. Unassigned is a real truck row. Residual readers: none.
 *   Chrome: Dashboard sub-nav (Overview | Assets & Crew), not a main rail item.
 *   Revolving copy never says "window." No invented vehicle or tenure fields.
 *   Unpaid is as-of the newest picked updatedAt in the sales-org zone.
 *   Null or missing grain is "no data", never $0.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Calculator,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Phone,
  Wallet
} from 'lucide-react';
import {
  ChartEmpty,
  ReportingLineChart
} from '../../components/reporting/ReportingCharts';
import PackPage from '../../components/layouts/so/PackPage';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { usePackChrome } from '../../contexts/PackChromeContext';
import assetsCrewService from '../../services/api/assetsCrewService';
import { formatCurrency } from '../../utils/invoiceHelpers';
import {
  TIME_FILTERS,
  chartTimeZone,
  companyClockZone,
  previousWindowForFilter,
  salesOrgCompanies,
  timelineLabel,
  viewerTimeZone,
  windowForFilter
} from '../../utils/scope1Dashboard';
import {
  DATA_WHEN_AVAILABLE,
  EMPTY_WINDOW,
  FAILED_WINDOW,
  grainViewState,
  howManyOptionsForShow,
  MEASURES,
  MIN_CALL_COUNT_DEFAULT,
  MODAL_RANGE,
  PRIMARY_MEASURES,
  RANK_BY,
  REPORT_BY,
  REVOLVE_MS,
  SECONDARY_MEASURES,
  SHOW,
  applyDormantFilter,
  applyShowFilter,
  buildGrainRows,
  centsToDollars,
  compactAxisCount,
  compactAxisDollars,
  historyInRange,
  mergeGrains,
  modalFilterHasStoredPoint,
  modalFilterPhrase,
  modalTimeRange,
  timeFilterHasStoredPoint,
  presentExpenseKeys,
  resolveHowMany,
  coverageCompanyIds,
  grainFullyCovered,
  revolvingLines,
  sortByMetricDesc,
  storyText,
  topThreeByInvoice,
  highestYield,
  unpaidCardFromWindows,
  pickUnpaidHeadline,
  formatUnpaidNotice,
  formatUnpaidAsOf,
  yieldLine,
  valueLine
} from '../../utils/assetsCrew';

const DASHBOARD_SUBNAV = [
  { key: 'overview', label: 'Overview', to: '/so/dashboard' },
  { key: 'assets-crew', label: 'Assets & Crew', to: '/so/assets-crew' }
];
const FILTER_LABELS = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};
const EMPTY_COMPANIES = [];
const LIST_PAGE_SIZE = 20;
const BRAND = 'hsl(var(--brand-primary))';
const CHART_AXIS_FROM_ZERO = [0, 'auto'];
const CHART_METRICS = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'calls', label: 'Calls' }
];
const TILE_ICONS = {
  callCount: Phone,
  totalInvoiceCents: DollarSign,
  totalBalanceCents: Wallet,
  averageInvoicePerCallCents: Calculator
};

/**
 * @param {number} cents
 * @returns {string}
 */
function money(cents) {
  return formatCurrency(centsToDollars(cents));
}

/**
 * Unpaid callout. One headline. Fail closed is "no data".
 * A discreet "!" hover names the highest-priority case.
 * @param {{
 *   totalBalanceCents: number|null,
 *   asOf: string|null,
 *   notice: { kind: string, sentence: string }|null
 * }} props
 */
function UnpaidCard({ totalBalanceCents, asOf, notice }) {
  const noData = totalBalanceCents == null;
  return (
    <section
      className="relative rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid="unpaid-card"
    >
      <h2 className="text-md font-semibold text-gray-900">Unpaid</h2>
      {notice ? (
        <div className="group absolute right-3 top-3">
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold text-gray-500 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
            aria-label={notice.sentence}
            data-testid="unpaid-card-notice"
          >
            !
          </button>
          <p
            className="invisible absolute right-0 z-10 mt-1 w-64 rounded-md border border-gray-200 bg-white p-2 text-left text-xs text-gray-700 shadow-sm group-hover:visible group-focus-within:visible"
            data-testid="unpaid-card-notice-text"
          >
            {notice.sentence}
          </p>
        </div>
      ) : null}
      <p className="mt-2 text-2xl font-semibold text-gray-900" data-testid="unpaid-card-amount">
        {noData ? 'no data' : money(totalBalanceCents)}
      </p>
      <p className="mt-1 text-sm text-gray-500">Not yet paid on these calls</p>
      {asOf ? (
        <p className="mt-1 text-xs text-gray-400" data-testid="unpaid-card-as-of">{asOf}</p>
      ) : null}
    </section>
  );
}

/**
 * @param {object} measures
 * @param {string} key
 * @param {'count'|'cents'} kind
 * @returns {string}
 */
function formatMeasure(measures, key, kind) {
  const value = measures?.[key];
  if (key === 'totalBalanceCents' && value == null) return 'no data';
  if (kind === 'cents') return money(value);
  return String(value ?? 0);
}

/**
 * @param {object} row
 * @param {string|null} fallbackCompany
 * @returns {string}
 */
function modalHeader(row, fallbackCompany) {
  const company = row?.companyName || fallbackCompany || '';
  const name = row?.kind === 'truck' ? (row.name || 'Truck') : (row?.name || 'Name');
  return company ? `${name} · ${company}` : name;
}

/**
 * Show settings: popover on desktop, bottom sheet on small screens.
 * @param {object} props
 */
function ShowSettings({
  open,
  showMode,
  rankBy,
  howMany,
  minCallCount,
  ignoreLowVolume,
  onRankBy,
  onHowMany,
  onMinCallCount,
  onIgnoreLowVolume,
  onClose
}) {
  if (!open) return null;
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-30 bg-gray-900/30 sm:hidden"
        aria-label="Close settings"
        onClick={onClose}
      />
      <div
        className="z-40 w-full rounded-t-2xl border border-gray-200 bg-white p-4 shadow-lg max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 sm:absolute sm:left-0 sm:top-full sm:mt-2 sm:w-80 sm:rounded-xl"
        data-testid="show-settings"
        role="dialog"
        aria-label="Show settings"
      >
        <label className="block text-sm text-gray-600">
          Rank by
          <select
            value={rankBy}
            onChange={(event) => onRankBy(event.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900"
            aria-label="Rank by"
            data-testid="show-rank-by"
          >
            {RANK_BY.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        </label>
        {showMode === SHOW.all ? null : (
          <fieldset className="mt-3">
            <legend className="text-sm text-gray-600">How many</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {howManyOptionsForShow(showMode).map((item) => (
                <label
                  key={item.key}
                  className={`flex min-h-[44px] items-center justify-center rounded-lg border px-2 text-sm ${
                    howMany === item.key
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="how-many"
                    value={item.key}
                    checked={howMany === item.key}
                    onChange={() => onHowMany(item.key)}
                    className="sr-only"
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <label className="mt-4 flex min-h-[44px] items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={ignoreLowVolume}
            onChange={(event) => onIgnoreLowVolume(event.target.checked)}
            data-testid="ignore-low-volume"
          />
          Ignore low volume
        </label>
        {ignoreLowVolume ? (
          <label className="mt-2 block text-sm text-gray-600">
            Only include drivers/trucks with at least
            <input
              type="number"
              min={0}
              value={minCallCount}
              onChange={(event) => onMinCallCount(Number(event.target.value) || 0)}
              className="mx-2 w-16 rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
              data-testid="show-min-calls"
            />
            calls
          </label>
        ) : null}
      </div>
    </>
  );
}

/**
 * One modal pattern for truck and driver.
 * @param {object} props
 */
function AssetsCrewModal({
  row,
  peers,
  now,
  displayZone,
  companyName,
  onClose
}) {
  const [modalFilter, setModalFilter] = useState(MODAL_RANGE.weeks4);
  const [chartMetric, setChartMetric] = useState('invoice');
  const [seeMore, setSeeMore] = useState(false);
  const range = useMemo(
    () => modalTimeRange(modalFilter, now, displayZone),
    [modalFilter, now, displayZone]
  );
  const modalGrain = modalFilter === MODAL_RANGE.months4 ? 'month' : 'week';
  const weekAvailable = modalFilterHasStoredPoint(
    row?.windows,
    MODAL_RANGE.weeks4,
    now,
    displayZone
  );
  const monthAvailable = modalFilterHasStoredPoint(
    row?.windows,
    MODAL_RANGE.months4,
    now,
    displayZone
  );
  useEffect(() => {
    if (modalFilter === MODAL_RANGE.weeks4 && !weekAvailable && monthAvailable) {
      setModalFilter(MODAL_RANGE.months4);
    }
  }, [modalFilter, weekAvailable, monthAvailable]);
  const modalView = useMemo(
    () => grainViewState({
      allWindows: row?.windows,
      grainWindows: row?.windows,
      filter: modalGrain,
      range,
      timeZone: displayZone
    }),
    [row, modalGrain, range, displayZone]
  );
  const chart = useMemo(
    () => historyInRange(row, range, displayZone, chartMetric, {
      modalFilter,
      now
    }),
    [row, range, displayZone, chartMetric, modalFilter, now]
  );
  const currentPoint = chart.find((point) => point.inProgress) || null;
  const measures = currentPoint?.measures ?? null;
  const missingModal = currentPoint == null;
  const peerViews = useMemo(
    () => (peers || []).map((peer) => {
      const points = historyInRange(peer, range, displayZone, 'invoice', {
        modalFilter,
        now
      });
      const current = points.find((point) => point.inProgress);
      return {
        ...peer,
        measures: current?.measures ?? peer.measures
      };
    }),
    [peers, range, displayZone, modalFilter, now]
  );
  const story = useMemo(
    () => (missingModal ? null : storyText({
      row,
      measures,
      peers: peerViews,
      peerMeasures: peerViews,
      modalFilter
    })),
    [missingModal, row, measures, peerViews, modalFilter]
  );
  const verdict = useMemo(
    () => (missingModal ? null : valueLine({
      row,
      measures,
      peers: peerViews,
      peerMeasures: peerViews,
      modalFilter
    })),
    [missingModal, row, measures, peerViews, modalFilter]
  );
  const modalReason = missingModal && modalView.kind === 'missing'
    ? modalView.reason
    : null;
  const modalNote = modalView.kind === 'partial' ? modalView.note : null;
  const modalEmptyText = modalReason || (missingModal ? EMPTY_WINDOW : null);
  const metricLabel = chartMetric === 'calls' ? 'Calls' : 'Invoice';
  const caption = `${metricLabel} — last 4 complete plus Current`;
  const expenseKeys = presentExpenseKeys(currentPoint?.windows || []);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" data-testid="assets-crew-modal-root">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/40"
        aria-label="Dismiss modal"
        onClick={onClose}
      />
      <section
        className="relative z-10 flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
        data-testid="assets-crew-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assets-crew-modal-title"
      >
        <div className="shrink-0 px-5 pt-5 sm:px-6 sm:pt-6">
          <h2 id="assets-crew-modal-title" className="text-lg font-semibold text-gray-900">
            {modalHeader(row, companyName)}
          </h2>
          <div
            className="mt-3 inline-flex overflow-hidden rounded-lg border border-gray-200 text-sm"
            role="group"
            aria-label="Modal time filter"
            data-testid="modal-time-filter"
          >
            {[
              { value: MODAL_RANGE.weeks4, label: 'Week', available: weekAvailable },
              { value: MODAL_RANGE.months4, label: 'Month', available: monthAvailable }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                disabled={!option.available}
                title={option.available ? undefined : DATA_WHEN_AVAILABLE}
                className={`px-3 py-1 ${
                  !option.available
                    ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                    : modalFilter === option.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700'
                }`}
                data-testid={`modal-filter-${option.value}`}
                onClick={() => {
                  if (!option.available) return;
                  setModalFilter(option.value);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6 sm:pb-6">
        {modalEmptyText ? (
          <div className="mt-4 h-28 rounded-2xl border border-gray-200 bg-white" data-testid="modal-primary-tiles">
            <ChartEmpty text={modalEmptyText} />
          </div>
        ) : (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="modal-primary-tiles">
          {PRIMARY_MEASURES.map((measure) => {
            const Icon = TILE_ICONS[measure.key] || DollarSign;
            return (
              <div
                key={measure.key}
                className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm"
                data-testid={`tile-${measure.key}`}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </div>
                <p className="min-w-0 flex-1 text-xs text-gray-500">{measure.label}</p>
                <p className="shrink-0 text-sm font-semibold text-gray-900">
                  {formatMeasure(measures, measure.key, measure.kind)}
                </p>
              </div>
            );
          })}
        </div>
        )}

        {!modalEmptyText && seeMore ? (
          <div
            className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            data-testid="modal-secondary-tiles"
          >
            <dl className="grid grid-cols-2">
              {[
                ...SECONDARY_MEASURES.map((measure) => ({
                  key: measure.key,
                  label: measure.label,
                  value: formatMeasure(measures, measure.key, measure.kind)
                })),
                ...expenseKeys.map((key) => ({
                  key,
                  label: key,
                  value: money(measures.expenses?.[key] || 0)
                }))
              ].map((item, index) => (
                <div
                  key={item.key}
                  className={`flex items-center justify-between gap-2 px-3 py-2 ${
                    index % 2 === 1 ? 'border-l border-gray-200' : ''
                  } ${index >= 2 ? 'border-t border-gray-200' : ''}`}
                >
                  <dt className="text-xs text-gray-500">{item.label}</dt>
                  <dd className="shrink-0 text-sm font-semibold text-gray-900">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        {!modalEmptyText ? (
        <button
          type="button"
          className="mt-3 flex items-center justify-center gap-1 py-1 text-sm text-gray-600"
          data-testid="see-more"
          onClick={() => setSeeMore((open) => !open)}
        >
          {seeMore ? 'See less' : 'See more'}
          <ChevronDown className={`h-4 w-4 ${seeMore ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
        ) : null}

        {story ? (
        <div className="mt-4 text-sm text-gray-700" data-testid="modal-story">
          <p>
            <span className="font-bold text-gray-900">Insight: </span>
            {story}
          </p>
        </div>
        ) : null}

        {modalNote ? (
          <p className="mt-3 text-sm text-gray-500" data-testid="modal-partial-note">{modalNote}</p>
        ) : null}

        <div className="mt-4 flex items-baseline justify-between gap-3">
          <p className="text-sm text-gray-500" data-testid="chart-caption">
            {caption}
          </p>
        </div>
        <div className="mt-1 h-36 shrink-0" data-testid="history-strip">
          {chart.length > 0 ? (
            <ReportingLineChart
              data={chart}
              dataKey="value"
              name={metricLabel}
              color={BRAND}
              domain={CHART_AXIS_FROM_ZERO}
              valueFormatter={chartMetric === 'calls' ? compactAxisCount : compactAxisDollars}
              tooltipValueOnly
            />
          ) : (
            <ChartEmpty text={modalEmptyText || EMPTY_WINDOW} />
          )}
        </div>
        <div className="mt-3 flex justify-center">
          <div
            className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-sm"
            role="group"
            aria-label="Chart metric"
            data-testid="chart-metric-filter"
          >
            {CHART_METRICS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`px-3 py-1 ${
                  chartMetric === option.value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
                }`}
                data-testid={`chart-metric-${option.value}`}
                onClick={() => setChartMetric(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {verdict ? (
        <p className="mt-3 text-sm text-gray-700" data-testid="value-line">
          <span className="font-bold text-gray-900">Performance: </span>
          {verdict}
        </p>
        ) : null}
        <p className="sr-only">{modalFilterPhrase(modalFilter)}</p>
        <div className="mt-6 text-right">
          <button
            type="button"
            className="min-h-[44px] text-sm text-gray-500"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        </div>
      </section>
    </div>
  );
}

export default function AssetsCrewPage({
  now: nowProp,
  timeZone: timeZoneProp
}) {
  const { towtechSoChrome, loading: packLoading } = usePackChrome();
  const { companyScope, activeCompanyId, companies, salesOrganization } = useAuth();
  const { company } = useCompany();
  const now = useMemo(() => nowProp || new Date(), [nowProp]);
  const orgCompanies = useMemo(
    () => salesOrgCompanies(companies || EMPTY_COMPANIES),
    [companies]
  );
  const isAll = companyScope === 'all';
  const [filter, setFilter] = useState('day');
  const [reportBy, setReportBy] = useState(REPORT_BY.driver);
  const [showMode, setShowMode] = useState(SHOW.all);
  const [rankBy, setRankBy] = useState('totalInvoiceCents');
  const [howMany, setHowMany] = useState('top10');
  const [minCallCount, setMinCallCount] = useState(MIN_CALL_COUNT_DEFAULT);
  const [ignoreLowVolume, setIgnoreLowVolume] = useState(true);
  const [allIgnoreLowVolume, setAllIgnoreLowVolume] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const showControlRef = useRef(null);
  const [payloads, setPayloads] = useState([]);
  const [status, setStatus] = useState('loading');
  const [selected, setSelected] = useState(null);
  const [revolveIndex, setRevolveIndex] = useState(0);
  const [listPage, setListPage] = useState(1);

  const listedCompany = useMemo(
    () => orgCompanies.find((row) => row.id === (company?.id || activeCompanyId)) || null,
    [orgCompanies, company?.id, activeCompanyId]
  );
  const displayZone = useMemo(() => {
    if (timeZoneProp) return chartTimeZone(timeZoneProp);
    if (isAll) return viewerTimeZone();
    return chartTimeZone(companyClockZone(listedCompany));
  }, [timeZoneProp, isAll, listedCompany]);
  const asOfZone = useMemo(
    () => chartTimeZone(salesOrganization?.timezone || displayZone),
    [salesOrganization?.timezone, displayZone]
  );

  const currentRange = useMemo(
    () => windowForFilter(filter, now, displayZone),
    [filter, now, displayZone]
  );
  const previousRange = useMemo(
    () => previousWindowForFilter(filter, now, displayZone),
    [filter, now, displayZone]
  );

  const fanoutIds = useMemo(
    () => (isAll ? orgCompanies.map((row) => row.id) : [activeCompanyId].filter(Boolean)),
    [isAll, orgCompanies, activeCompanyId]
  );

  useEffect(() => {
    let cancelled = false;
    setPayloads([]);
    setSelected(null);
    setStatus('loading');
    if (fanoutIds.length === 0) {
      setStatus('ready');
      return undefined;
    }

    Promise.allSettled(fanoutIds.map((companyId) => (
      assetsCrewService.getAssetsCrew({ companyId })
    )))
      .then((results) => {
        if (cancelled) return;
        if (results.some((result) => result.status !== 'fulfilled' || result.value == null)) {
          setStatus('failed');
          return;
        }
        setPayloads(results.map((result, index) => ({
          ...(result.value?.data || result.value || {}),
          companyId: result.value?.data?.companyId || fanoutIds[index]
        })));
        setStatus('ready');
      });

    return () => {
      cancelled = true;
    };
  }, [fanoutIds]);

  useEffect(() => {
    setListPage(1);
  }, [reportBy, filter, showMode, rankBy, howMany, ignoreLowVolume, allIgnoreLowVolume, minCallCount, activeCompanyId, companyScope]);

  const grain = useMemo(() => {
    if (status !== 'ready') return { empty: true, rows: [] };
    return mergeGrains(payloads.map((payload) => buildGrainRows(payload, {
      reportBy,
      range: currentRange,
      filter,
      now,
      timeZone: displayZone,
      companyName: orgCompanies.find((row) => row.id === payload.companyId)?.businessName || null
    })));
  }, [status, payloads, reportBy, currentRange, filter, now, displayZone, orgCompanies]);

  const liveRows = useMemo(() => {
    if (grain.empty) return [];
    return applyDormantFilter(grain.rows, now, reportBy);
  }, [grain, now, reportBy]);
  const allWindows = useMemo(
    () => payloads.flatMap((payload) => (
      (payload.windows || []).map((windowRow) => ({
        ...windowRow,
        companyId: windowRow.companyId || payload.companyId
      }))
    )),
    [payloads]
  );

  const visibleRows = useMemo(() => {
    const filtered = applyShowFilter(liveRows, {
      mode: showMode,
      rankBy,
      howMany,
      ignoreLowVolume,
      minCallCount
    });
    if (showMode === SHOW.all) {
      const rows = allIgnoreLowVolume
        ? filtered.filter((row) => Number(row.measures?.callCount) >= minCallCount)
        : filtered;
      return sortByMetricDesc(rows, rankBy);
    }
    return sortByMetricDesc(filtered, rankBy);
  }, [liveRows, showMode, rankBy, howMany, ignoreLowVolume, allIgnoreLowVolume, minCallCount]);

  const listPageCount = Math.max(1, Math.ceil(visibleRows.length / LIST_PAGE_SIZE));
  const listPageSafe = Math.min(listPage, listPageCount);
  const pageRows = visibleRows.slice(
    (listPageSafe - 1) * LIST_PAGE_SIZE,
    listPageSafe * LIST_PAGE_SIZE
  );

  const expenseKeys = useMemo(
    () => presentExpenseKeys(visibleRows.flatMap((row) => row.windows || [])),
    [visibleRows]
  );
  const topThree = useMemo(() => topThreeByInvoice(liveRows), [liveRows]);
  const yieldRows = useMemo(() => highestYield(liveRows), [liveRows]);
  const invoiceCoverageCompanyIds = useMemo(
    () => coverageCompanyIds(allWindows, filter, [currentRange, previousRange]),
    [allWindows, filter, currentRange, previousRange]
  );
  const callouts = useMemo(
    () => revolvingLines({
      rows: liveRows,
      reportBy,
      filter,
      currentRange,
      previousRange,
      timeZone: displayZone,
      now,
      invoiceComparable: grainFullyCovered(allWindows, filter, currentRange, displayZone, {
        companyIds: invoiceCoverageCompanyIds
      })
        && grainFullyCovered(allWindows, filter, previousRange, displayZone, {
          companyIds: invoiceCoverageCompanyIds
        })
    }),
    [liveRows, reportBy, filter, currentRange, previousRange, displayZone, now, allWindows, invoiceCoverageCompanyIds]
  );

  useEffect(() => {
    setRevolveIndex(0);
  }, [filter, reportBy, currentRange, liveRows.length]);

  useEffect(() => {
    if (callouts.length < 2) return undefined;
    const timer = setInterval(() => {
      setRevolveIndex((index) => (index + 1) % callouts.length);
    }, REVOLVE_MS);
    return () => clearInterval(timer);
  }, [callouts.length]);

  const driverCount = useMemo(() => {
    if (status !== 'ready') return 0;
    const built = mergeGrains(payloads.map((payload) => buildGrainRows(payload, {
      reportBy: REPORT_BY.driver,
      range: currentRange,
      filter,
      now,
      timeZone: displayZone,
      companyName: null
    })));
    return applyDormantFilter(built.rows, now, REPORT_BY.driver).length;
  }, [status, payloads, currentRange, filter, now, displayZone]);
  const truckCount = useMemo(() => {
    if (status !== 'ready') return 0;
    const built = mergeGrains(payloads.map((payload) => buildGrainRows(payload, {
      reportBy: REPORT_BY.truck,
      range: currentRange,
      filter,
      now,
      timeZone: displayZone,
      companyName: null
    })));
    return applyDormantFilter(built.rows, now, REPORT_BY.truck).length;
  }, [status, payloads, currentRange, filter, now, displayZone]);

  const pickShow = (mode) => {
    if (settingsOpen && showMode === mode) {
      setSettingsOpen(false);
      return;
    }
    setShowMode(mode);
    if (mode !== SHOW.all) {
      setHowMany(resolveHowMany({ howMany, mode }).key);
    }
    setSettingsOpen(true);
  };

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onPointerDown = (event) => {
      if (!showControlRef.current?.contains(event.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [settingsOpen]);

  const filterAvailable = useMemo(() => {
    const next = {};
    TIME_FILTERS.forEach((value) => {
      next[value] = timeFilterHasStoredPoint(
        allWindows,
        value,
        windowForFilter(value, now, displayZone),
        { timeZone: displayZone, now }
      );
    });
    return next;
  }, [allWindows, now, displayZone]);

  const unpaidDriver = useMemo(() => unpaidCardFromWindows(allWindows, {
    reportBy: REPORT_BY.driver,
    range: currentRange,
    filter,
    timeZone: displayZone,
    now
  }), [allWindows, currentRange, filter, displayZone, now]);
  const unpaidTruck = useMemo(() => unpaidCardFromWindows(allWindows, {
    reportBy: REPORT_BY.truck,
    range: currentRange,
    filter,
    timeZone: displayZone,
    now
  }), [allWindows, currentRange, filter, displayZone, now]);
  const unpaidHeadline = useMemo(
    () => pickUnpaidHeadline(unpaidDriver, unpaidTruck),
    [unpaidDriver, unpaidTruck]
  );
  const unpaidNotice = useMemo(() => {
    const rank = {
      setup_incomplete: 0,
      auth_failed: 1,
      disconnected: 2,
      paused: 3,
      schedule_paused: 4
    };
    const collections = payloads
      .map((payload) => payload.unpaidNotice?.collection)
      .filter(Boolean)
      .sort((left, right) => (rank[left.kind] ?? 9) - (rank[right.kind] ?? 9));
    const failedHours = payloads.flatMap((payload) => (
      payload.unpaidNotice?.failedHours || []
    ));
    return formatUnpaidNotice({
      collection: collections[0] || null,
      failedHours,
      driverCents: unpaidHeadline.driverCents,
      truckCents: unpaidHeadline.truckCents,
      filter,
      timeZone: asOfZone
    });
  }, [payloads, unpaidHeadline, filter, asOfZone]);
  const unpaidAsOf = formatUnpaidAsOf(unpaidHeadline.updatedAt, asOfZone, now);

  if (packLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }
  if (!towtechSoChrome) {
    return <Navigate to="/so/dashboard" replace />;
  }

  const missingGrain = Boolean(grain.empty && grain.reason);
  const revolving = (!grain.empty && !missingGrain)
    ? (callouts[revolveIndex] || callouts[0] || null)
    : null;

  return (
    <PackPage title="Assets & Crew" items={DASHBOARD_SUBNAV}>
      <div className="flex flex-wrap items-center gap-3" data-testid="assets-crew-filters">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Time
          <select
            value={filter}
            onChange={(event) => {
              const value = event.target.value;
              if (filterAvailable[value] !== true) return;
              setFilter(value);
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-gray-900"
            aria-label="Time filter"
            data-testid="time-filter"
          >
            {TIME_FILTERS.map((value) => {
              const available = filterAvailable[value] === true;
              return (
                <option
                  key={value}
                  value={value}
                  disabled={!available}
                  title={available ? undefined : DATA_WHEN_AVAILABLE}
                  data-testid={`time-filter-${value}`}
                >
                  {FILTER_LABELS[value]}
                </option>
              );
            })}
          </select>
        </label>
        <div className="flex overflow-hidden rounded-lg border border-gray-200 text-sm" role="group" aria-label="Report by">
          {[
            { value: REPORT_BY.driver, label: `Drivers (${driverCount})` },
            { value: REPORT_BY.truck, label: `Trucks (${truckCount})` }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              className={`px-3 py-1 ${
                reportBy === option.value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
              }`}
              data-testid={`${option.value}-toggle`}
              onClick={() => setReportBy(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="relative" data-testid="show-control" ref={showControlRef}>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span>Show:</span>
            <div className="flex overflow-hidden rounded-lg border border-gray-200" role="group" aria-label="Show">
              {[
                { value: SHOW.all, label: 'All' },
                { value: SHOW.top, label: 'Top performers' },
                { value: SHOW.bottom, label: 'Bottom performers' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`px-3 py-1 ${
                    showMode === option.value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
                  }`}
                  data-testid={`show-${option.value}`}
                  onClick={() => pickShow(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <ShowSettings
            open={settingsOpen}
            showMode={showMode}
            rankBy={rankBy}
            howMany={howMany}
            minCallCount={minCallCount}
            ignoreLowVolume={showMode === SHOW.all ? allIgnoreLowVolume : ignoreLowVolume}
            onRankBy={setRankBy}
            onHowMany={setHowMany}
            onMinCallCount={setMinCallCount}
            onIgnoreLowVolume={showMode === SHOW.all ? setAllIgnoreLowVolume : setIgnoreLowVolume}
            onClose={() => setSettingsOpen(false)}
          />
        </div>
      </div>
      <p className="mt-5 text-sm font-medium text-gray-700" data-testid="assets-crew-timeline">
        {timelineLabel(filter)}
      </p>

      {status === 'failed' ? (
        <div className="mt-6 h-72 rounded-2xl border border-gray-200 bg-white" data-testid="assets-crew-failed">
          <ChartEmpty text={FAILED_WINDOW} />
        </div>
      ) : null}

      {status === 'ready' && grain.empty && !grain.reason ? (
        <div className="mt-6 h-72 rounded-2xl border border-gray-200 bg-white" data-testid="assets-crew-empty">
          <ChartEmpty text={EMPTY_WINDOW} />
        </div>
      ) : null}

      {status === 'ready' && missingGrain ? (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="assets-crew-callouts">
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-md font-semibold text-gray-900">Top 3 by Total Invoice</h2>
              <p className="mt-2 text-sm text-gray-500" data-testid="top-three">{grain.reason}</p>
            </section>
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-md font-semibold text-gray-900">Highest Yield</h2>
              <p className="mt-3 text-sm text-gray-500" data-testid="highest-yield">{grain.reason}</p>
            </section>
            <UnpaidCard
              totalBalanceCents={unpaidHeadline.totalBalanceCents}
              asOf={unpaidAsOf}
              notice={unpaidNotice}
            />
          </div>
          <div className="mt-3 h-72 rounded-2xl border border-gray-200 bg-white" data-testid="assets-crew-grain-reason">
            <ChartEmpty text={grain.reason} />
          </div>
        </>
      ) : null}

      {status === 'ready' && !grain.empty ? (
        <>
          {grain.reason ? (
            <p className="mt-4 text-sm text-gray-500" data-testid="grain-partial-note">{grain.reason}</p>
          ) : null}
          <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="assets-crew-callouts">
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-md font-semibold text-gray-900">Top 3 by Total Invoice</h2>
              <ol className="mt-2 space-y-0.5" data-testid="top-three">
                {topThree.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between py-1 text-left text-sm text-gray-900"
                      onClick={() => setSelected(row)}
                    >
                      <span className="font-medium">{row.name}</span>
                      <span>{money(row.measures.totalInvoiceCents)}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-md font-semibold text-gray-900">Highest Yield</h2>
              {yieldRows.length > 0 ? (
                <ol className="mt-2 space-y-0.5" data-testid="highest-yield">
                  {yieldRows.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="w-full py-1 text-left text-sm text-gray-900"
                        onClick={() => setSelected(row)}
                      >
                        {yieldLine(row)}
                      </button>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-3 text-sm text-gray-500" data-testid="highest-yield">
                  Not enough calls yet to rank yield.
                </p>
              )}
            </section>
            <UnpaidCard
              totalBalanceCents={unpaidHeadline.totalBalanceCents}
              asOf={unpaidAsOf}
              notice={unpaidNotice}
            />
          </div>
          {revolving ? (
            <section
              className="mt-3 flex flex-col gap-0.5 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-baseline sm:gap-3"
              data-testid="revolving-callout"
            >
              <h2 className="shrink-0 text-md font-semibold text-gray-900">Right now</h2>
              <p className="text-sm text-gray-600">{revolving}</p>
            </section>
          ) : null}

          <div className="mt-3 overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full text-sm" data-testid="assets-crew-table">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  {MEASURES.map((measure) => (
                    <th key={measure.key} className="px-4 py-3 font-medium">{measure.label}</th>
                  ))}
                  {expenseKeys.map((key) => (
                    <th key={key} className="px-4 py-3 font-medium">{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer border-t border-gray-100 ${
                      row.unassigned ? 'text-gray-400' : 'text-gray-900'
                    }`}
                    data-testid={`assets-row-${row.id}`}
                    data-unassigned={row.unassigned ? 'true' : 'false'}
                    onClick={() => setSelected(row)}
                  >
                    <td className="px-4 py-3">
                      <div>{row.name}</div>
                      {isAll && row.companyName ? (
                        <div className="text-xs text-gray-500">{row.companyName}</div>
                      ) : null}
                    </td>
                    {MEASURES.map((measure) => (
                      <td key={measure.key} className="px-4 py-3">
                        {formatMeasure(row.measures, measure.key, measure.kind)}
                      </td>
                    ))}
                    {expenseKeys.map((key) => (
                      <td key={key} className="px-4 py-3">
                        {money(row.measures.expenses?.[key] || 0)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {visibleRows.length > LIST_PAGE_SIZE ? (
              <div
                className="flex items-center justify-between border-t border-gray-200 px-6 py-4"
                data-testid="assets-crew-pager"
              >
                <p className="text-sm text-gray-500">
                  Showing {((listPageSafe - 1) * LIST_PAGE_SIZE) + 1}
                  {' to '}
                  {Math.min(listPageSafe * LIST_PAGE_SIZE, visibleRows.length)}
                  {' of '}
                  {visibleRows.length}
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="p-2 text-sm text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="assets-crew-page-prev"
                    aria-label="Previous"
                    disabled={listPageSafe === 1}
                    onClick={() => setListPage(listPageSafe - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {listPageSafe} of {listPageCount}
                  </span>
                  <button
                    type="button"
                    className="p-2 text-sm text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="assets-crew-page-next"
                    aria-label="Next"
                    disabled={listPageSafe === listPageCount}
                    onClick={() => setListPage(listPageSafe + 1)}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {selected ? (
        <AssetsCrewModal
          row={selected}
          peers={liveRows}
          now={now}
          displayZone={displayZone}
          companyName={listedCompany?.businessName || selected.companyName || null}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </PackPage>
  );
}
